import { useGSAP } from "@gsap/react";
import { useRef } from "react";
import gsap from "gsap";

export default function ArticleEffect() {
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const container = containerRef.current;
      if (!container) return;

      const { offsetWidth, offsetHeight } = container;
      const stars = container.querySelectorAll(".star");

      stars.forEach((star, idx) => {
        const element = star as HTMLElement;

        // 별똥별 이동 시간 (3-7초 랜덤)
        const duration = Math.random() * 10 + 3;

        // 좌측 하단 랜덤 시작 위치 생성 함수
        const getRandomStart = () => ({
          x: Math.random() * (offsetWidth * 0.2), // 좌측 20% 영역
          y: offsetHeight * 0.8 + Math.random() * (offsetHeight * 0.2), // 하단 20% 영역
        });

        // 우측 상단 랜덤 끝 위치 생성 함수
        const getRandomEnd = () => ({
          x: offsetWidth * 0.8 + Math.random() * (offsetWidth * 0.5), // 우측 20% 영역
          y: Math.random() * (offsetHeight * 0.5), // 상단 20% 영역
        });

        const startPos = getRandomStart();

        // 초기 설정: 좌측 하단, 투명, 작게
        gsap.set(element, {
          x: -10,
          y: startPos.y,
          opacity: 0,
          scale: 0.3,
        });

        // 무한 루프 타임라인
        const tl = gsap.timeline({
          repeat: -1, // 무한 반복
          delay: idx * 0.3, // 순차적 시작 (0.3초씩 지연)
          onRepeat: () => {
            // 반복될 때마다 새로운 시작/끝 위치 설정
            const newStart = getRandomStart();
            gsap.set(element, { x: newStart.x, y: newStart.y });
            tl.invalidate(); // 타임라인 값 갱신
          },
        });

        // 1. 페이드인 + 크기 커지기 (0.8초)
        tl.to(element, {
          opacity: 1, // 완전 불투명
          scale: 1,
          duration: 0.8,
          delay: 1.5,
          ease: "power4.out",
        })
          // 2. 좌측 하단 → 우측 상단 이동 (duration초)
          .to(
            element,
            {
              x: getRandomEnd().x,
              y: getRandomEnd().y,
              duration: duration,
              ease: "none", // 등속 운동
            },
            "-=0.5" // 페이드인과 0.5초 겹침
          )
          // 3. 페이드아웃 + 작아지기 (1초)
          .to(
            element,
            {
              opacity: 0,
              scale: 0.3,
              duration: 1,
              ease: "power2.in",
            },
            "-=1.5" // 이동 끝나기 1.5초 전부터 사라지기 시작
          );

        // 약간의 크기 변화 (반짝임 효과)
        gsap.to(element, {
          scale: `*=${0.95 + Math.random() * 0.1}`, // 미세한 변화
          duration: 1.5,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });

        // 별 주변 빛 깜빡임
        const afterElement = element.querySelector(".star-glow") as HTMLElement;
        if (afterElement) {
          gsap.to(afterElement, {
            opacity: 0.5, // 0.5~1 사이 변화 (더 밝게)
            duration: 1 + Math.random(), // 1~2초 랜덤
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
          });

          // 빛 크기 변화
          gsap.to(afterElement, {
            scale: 1.2,
            duration: 1.5,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
          });
        }
      });
    },
    { scope: containerRef }
  );

  const starCount = Array.from({ length: 10 });

  return (
    <div
      ref={containerRef}
      className="relative w-full h-2/3 overflow-hidden z-15"
    >
      {starCount.map((_, idx) => (
        <div
          key={`star_${idx}`}
          className="star absolute w-1 h-1 bg-white rounded-full z-15 shadow-lg shadow-white/50"
        >
          <div className="star-glow absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white/70 blur-md" />
        </div>
      ))}
    </div>
  );
}
